import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { AuthCard } from '@/components/marketing/auth-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { auth } from '@/lib/auth';
import { useClients, useMe, type Client } from '@/lib/clients';
import { GST_STATES, gstinProblem, stateLabel } from '@/lib/gst';
import { useT } from '@/lib/i18n';
import { ltrIn } from '@/lib/sales';
import { EMIRATES, emirateLabel } from '@/lib/settings';
import { useOpenClient } from '@/lib/use-open-client';

/**
 * W5 · Set up your company (document 06, ADR-0021): after sign-up, a user with no company names
 * theirs and becomes its owner. The shell sends a non-staff user without companies here.
 */
export const Route = createFileRoute('/onboarding')({ component: OnboardingPage });

interface CompanyForm {
  country: 'IN' | 'AE';
  name: string;
  stateCode: string;
  gstin: string;
  emirate: string;
  trn: string;
}

const EMPTY: CompanyForm = {
  country: 'IN',
  name: '',
  stateCode: '29',
  gstin: '',
  emirate: 'Dubai',
  trn: '',
};

function OnboardingPage() {
  const qc = useQueryClient();
  const openClient = useOpenClient();
  const me = useMe(auth.enabled).data;
  const navigate = useNavigate();
  const t = useT();
  const clients = useClients(auth.enabled);
  // someone who already has a company (a second tab, the back button) goes to their books
  const hasCompany = Boolean(!clients.isFetching && clients.data?.length);
  const [form, setForm] = useState<CompanyForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof CompanyForm, string>>>({});
  const set = (k: keyof CompanyForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const india = form.country === 'IN';

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Client>('/practice/onboarding', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (client) => {
      // put the new company in the cache before leaving, so the shell never reads the old empty
      // list and sends the user straight back here
      qc.setQueryData<Client[]>(['practice', 'clients'], (old) => [
        ...(old ?? []).filter((c) => c.id !== client.id),
        client,
      ]);
      void qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      openClient(client.id, { toDashboard: true });
    },
  });
  const busy = create.isPending || create.isSuccess;
  useEffect(() => {
    if (hasCompany && !busy) void navigate({ to: '/dashboard', replace: true });
  }, [hasCompany, busy, navigate]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (form.name.trim().length < 2) next.name = t('publicPages.onboarding.nameRequired');
    const gstin = form.gstin.replace(/\s+/g, '').toUpperCase();
    const trn = form.trn.replace(/\s+/g, '');
    if (india && gstin) {
      const problem = gstinProblem(gstin);
      if (problem) next.gstin = problem;
      else if (gstin.slice(0, 2) !== form.stateCode)
        next.stateCode = t('publicPages.onboarding.gstinState', {
          state: ltrIn(stateLabel(gstin.slice(0, 2))),
        });
    }
    if (!india && trn && !/^[0-9]{15}$/.test(trn)) next.trn = t('publicPages.onboarding.trnDigits');
    setErrors(next);
    if (Object.keys(next).length) return;
    create.mutate({
      name: form.name.trim(),
      country: form.country,
      ...(india
        ? { stateCode: form.stateCode, ...(gstin ? { gstin } : {}) }
        : { emirate: form.emirate, ...(trn ? { trn } : {}) }),
    });
  }

  const serverError =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? String(create.error)
        : null;

  return (
    <AuthCard
      title={t('publicPages.onboarding.title')}
      subtitle={t('publicPages.onboarding.subtitle')}
      signedIn
      footer={
        <span className="text-brand-slate">
          {me ? t('publicPages.onboarding.signedInAs', { email: ltrIn(me.email) }) : null}
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={() => void auth.signOut()}
          >
            {t('publicPages.onboarding.logOut')}
          </button>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-form">
        <ErrorBanner>{serverError}</ErrorBanner>
        <Field label={t('publicPages.onboarding.companyName')} error={errors.name}>
          {(id) => (
            <Input
              id={id}
              value={form.name}
              maxLength={200}
              autoFocus
              onChange={(e) => set('name')(e.target.value)}
              aria-invalid={Boolean(errors.name)}
              className="h-10 bg-white"
              data-testid="onboarding-name"
            />
          )}
        </Field>
        <Field
          label={t('publicPages.onboarding.country')}
          hint={t('publicPages.onboarding.countryHint')}
        >
          {(id) => (
            <NativeSelect
              id={id}
              value={form.country}
              onChange={(e) => {
                set('country')(e.target.value);
                setErrors({});
              }}
            >
              <option value="IN">{t('salesPages.country.IN')}</option>
              <option value="AE">{t('salesPages.country.AE')}</option>
            </NativeSelect>
          )}
        </Field>
        {india ? (
          <>
            <Field label={t('publicPages.onboarding.state')} error={errors.stateCode}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.stateCode}
                  onChange={(e) => set('stateCode')(e.target.value)}
                >
                  {GST_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field
              label={t('publicPages.onboarding.gstin')}
              hint={t('publicPages.onboarding.gstinHint')}
              error={errors.gstin}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.gstin}
                  placeholder="27AAGCG4321K1Z5"
                  className="h-10 bg-white"
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    set('gstin')(v);
                    const code = v.replace(/\s+/g, '').slice(0, 2);
                    if (/^\d{2}$/.test(code) && GST_STATES.some(([c]) => c === code))
                      set('stateCode')(code);
                  }}
                  aria-invalid={Boolean(errors.gstin)}
                />
              )}
            </Field>
          </>
        ) : (
          <>
            <Field label={t('publicPages.onboarding.emirate')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.emirate}
                  onChange={(e) => set('emirate')(e.target.value)}
                >
                  {EMIRATES.map((em) => (
                    <option key={em} value={em}>
                      {emirateLabel(em)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field
              label={t('publicPages.onboarding.trn')}
              hint={t('publicPages.onboarding.trnHint')}
              error={errors.trn}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.trn}
                  inputMode="numeric"
                  placeholder="100 1234567 00 003"
                  className="h-10 bg-white"
                  onChange={(e) => set('trn')(e.target.value)}
                  aria-invalid={Boolean(errors.trn)}
                />
              )}
            </Field>
          </>
        )}
        <Button type="submit" className="h-10 w-full font-semibold" disabled={busy}>
          {busy ? t('publicPages.onboarding.creating') : t('publicPages.onboarding.create')}
        </Button>
      </form>
    </AuthCard>
  );
}
